import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Clock } from 'lucide-react';
import SEO from '../components/SEO';

const BlogsPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const blogPosts = [
    {
      id: 1,
      slug: 'protecting-brand-identity-digital-age',
      title: 'Protecting Your Brand Identity in the Digital Age',
      excerpt: 'Discover essential strategies for safeguarding your brand in an increasingly digital marketplace. Learn how to prevent infringement and maintain brand integrity online.',
      author: 'Sarah Mitchell',
      date: 'December 8, 2024',
      readTime: '7 min read',
      category: 'Brand Protection',
      image: 'https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79?w=800&h=600&fit=crop'
    },
    {
      id: 2,
      slug: 'startup-guide-trademark-registration',
      title: 'Startup Guide: Why Trademark Registration Should Be Your First Step',
      excerpt: 'Before you invest in marketing and product development, protect your brand. This guide explains why early trademark registration is crucial for startup success.',
      author: 'Michael Chen',
      date: 'December 5, 2024',
      readTime: '6 min read',
      category: 'Startups',
      image: 'https://images.unsplash.com/photo-1758518731457-5ef826b75b3b?w=800&h=600&fit=crop'
    },
    {
      id: 3,
      slug: 'trademark-infringement-what-to-do',
      title: 'Trademark Infringement: What to Do When Someone Copies Your Brand',
      excerpt: 'Step-by-step action plan for handling trademark infringement. Learn your legal options, from cease and desist letters to litigation strategies.',
      author: 'Emily Rodriguez',
      date: 'December 1, 2024',
      readTime: '8 min read',
      category: 'Legal Action',
      image: 'https://images.unsplash.com/photo-1551135049-8a33b5883817?w=800&h=600&fit=crop'
    },
    {
      id: 4,
      slug: 'building-valuable-brand-asset',
      title: 'Building a Valuable Brand Asset: The ROI of Trademark Protection',
      excerpt: 'Trademarks are more than legal protection - they\'re valuable business assets. Learn how trademark registration increases company valuation and investor appeal.',
      author: 'David Thompson',
      date: 'November 28, 2024',
      readTime: '5 min read',
      category: 'Business Strategy',
      image: 'https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?w=800&h=600&fit=crop'
    },
    {
      id: 5,
      slug: 'ecommerce-brand-protection-guide',
      title: 'E-commerce Brand Protection: Defending Your Brand on Amazon & Beyond',
      excerpt: 'Protect your brand from counterfeiters and unauthorized sellers on e-commerce platforms. Practical strategies for Amazon Brand Registry and marketplace enforcement.',
      author: 'Lisa Anderson',
      date: 'November 25, 2024',
      readTime: '9 min read',
      category: 'E-commerce',
      image: 'https://images.unsplash.com/photo-1764106813759-9ef7bf42a0af?w=800&h=600&fit=crop'
    },
    {
      id: 6,
      slug: 'common-trademark-pitfalls',
      title: 'Common Trademark Pitfalls That Could Cost You Thousands',
      excerpt: 'Avoid costly mistakes in the trademark process. Learn about the most common errors entrepreneurs make and how to prevent them from derailing your brand.',
      author: 'Robert Kim',
      date: 'November 20, 2024',
      readTime: '6 min read',
      category: 'Tips & Advice',
      image: 'https://images.unsplash.com/photo-1573757056004-065ad36e2cf4?w=800&h=600&fit=crop'
    }
  ];

  const categories = ['All', 'Brand Protection', 'Startups', 'Legal Action', 'Business Strategy', 'E-commerce', 'Tips & Advice'];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Expert Trademark Blog | Norfolk Trademark"
        description="Read expert articles on trademark registration, brand protection, and IP law from licensed attorneys."
        keywords="trademark blog, brand protection tips, IP law articles, trademark advice"
        canonical="/resources/blogs"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#FF035B] via-[#2F1A8E] to-[#FFA06B] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Trademark Insights & Resources
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Stay informed with expert articles, guides, and insights on trademark registration, protection, and intellectual property law
            </p>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-testid={`blog-tab-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-[#FF035B] text-white shadow-lg shadow-[#FF035B]/30'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-wrap justify-center gap-8">
          {blogPosts
            .filter(post => activeCategory === 'All' || post.category === activeCategory)
            .map((post) => (
            <article
              key={post.id}
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 group w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)]"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#FF035B] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Meta Info */}
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#FF035B] transition-colors">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                {/* Author & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{post.author}</span>
                  </div>
                  <Link
                    to={`/resources/blogs/${post.slug}`}
                    className="flex items-center gap-1 text-[#FF035B] font-semibold text-sm hover:gap-2 transition-all"
                  >
                    Read More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link to="/resources/blogs" onClick={() => { setActiveCategory('All'); window.scrollTo(0, 0); }} className="inline-block bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105">
            Load More Articles
          </Link>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Stay Updated with Our Newsletter
          </h2>
          <p className="text-gray-300 mb-8">
            Get the latest trademark insights, tips, and updates delivered to your inbox
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF035B]"
            />
            <button className="bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-bold px-6 py-3 rounded-lg transition-all duration-300">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogsPage;
